import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useGetEventByIdQuery } from "Services/EventApiSlice";
import { notifications } from "@mantine/notifications";
import { Loader, Center } from "@mantine/core";
import dayjs from "dayjs";

const cardStyle: React.CSSProperties = {
  background: "#fff",
  border: "1px solid #e6e6e6",
  padding: 16,
  borderRadius: 6,
};

const fieldStyle: React.CSSProperties = {
  display: "block",
  width: "100%",
  padding: "8px 10px",
  borderRadius: 4,
  border: "1px solid #d9d9d9",
  backgroundColor: "#f5f5f5",
  color: "#666",
};

const AdminEventsView: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const plant = process.env.REACT_APP_PLANT;

  const {
    data: event,
    isLoading,
    error,
  } = useGetEventByIdQuery({ id: Number(id!), plant }, { skip: !id });

  React.useEffect(() => {
    if (error) {
      notifications.show({
        message: (error as any)?.data?.message || "An error occurred",
        color: "red",
      });
    }
  }, [error]);

  if (isLoading) {
    return (
      <Center style={{ height: "400px" }}>
        <Loader size="lg" />
      </Center>
    );
  }

  if (!event) {
    return (
      <div className="max-w-4xl mx-auto py-6 px-4">
        <button
          onClick={() => navigate(-1)}
          className="text-blue-600 mb-3"
        >
          ← Back
        </button>
        <div className="text-center py-8 text-gray-500">
          Event not found
        </div>
      </div>
    );
  }

  const startDateTime =
    event.eventDate && event.eventStartTime
      ? dayjs(`${event.eventDate} ${event.eventStartTime}`, "DD/MM/YYYY HH:mm")
      : null;

  const endDateTime =
    (event.eventEndDate || event.eventDate) && event.eventEndTime
      ? dayjs(
          `${event.eventEndDate || event.eventDate} ${event.eventEndTime}`,
          "DD/MM/YYYY HH:mm"
        )
      : null;

  return (
    <div className="max-w-4xl mx-auto py-6 px-4">
      <button
        onClick={() => navigate(-1)}
        className="text-blue-600 mb-3"
      >
        ← Back
      </button>

      <h3 className="mb-4 text-lg font-semibold">View Event</h3>

      <div style={cardStyle}>

  {/* Event Name */}
  <div className="mb-4">
    <label className="block text-sm font-medium mb-1">Event Name</label>
    <input
      value={event.name || event.eventName || ""}
      style={fieldStyle}
      disabled
    />
  </div>

  {/* Event Description */}
  <div className="mb-6">
    <label className="block text-sm font-medium mb-1">
      Event Description
    </label>
    <textarea
      value={event.description || event.eventDescription || ""}
      style={{ ...fieldStyle, minHeight: 90 }}
      disabled
    />
  </div>

  {/* 2 Column Grid Section */}
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

    {/* Date & Time */}
    <div>
      <label className="block text-sm font-medium mb-1">Date & Time</label>
      <input
        value={
          startDateTime?.isValid()
            ? `${startDateTime.format("MMM D, YYYY hh:mm A")} - ${
                endDateTime?.isValid()
                  ? endDateTime.format("MMM D, YYYY hh:mm A")
                  : "—"
              }`
            : "—"
        }
        style={fieldStyle}
        disabled
      />
    </div>

    {/* Venue */}
    <div>
      <label className="block text-sm font-medium mb-1">Venue</label>
      <input
        value={event.venue || event.eventAddress || "Not specified"}
        style={fieldStyle}
        disabled
      />
    </div>

    {/* City */}
    <div>
      <label className="block text-sm font-medium mb-1">City</label>
      <input
        value={event.eventCity || "Not specified"}
        style={fieldStyle}
        disabled
      />
    </div>

    {/* Event Type */}
    <div>
      <label className="block text-sm font-medium mb-1">Event Type</label>
      <input
        value={event.eventType || "Not specified"}
        style={fieldStyle}
        disabled
      />
    </div>

    {/* Ticket Type */}
    <div>
      <label className="block text-sm font-medium mb-1">Ticket Type</label>
      <input
        value={event.ticketType || "Not specified"}
        style={fieldStyle}
        disabled
      />
    </div>

    {/* Audience */}
    <div>
      <label className="block text-sm font-medium mb-1">Audience</label>
      <input
        value={
          event.audience?.join(", ") ||
          event.audienceType ||
          "Not specified"
        }
        style={fieldStyle}
        disabled
      />
    </div>
  </div>

  {/* Images */}
  <div className="mt-8">
    <label className="block mb-3 font-medium">
      Images ({event.imageUrls?.length || 0})
    </label>

    {event.imageUrls && event.imageUrls.length > 0 ? (
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        {event.imageUrls.map((url, index) => (
          <img
            key={index}
            src={url}
            alt={`Event image ${index + 1}`}
            className="h-40 w-full object-cover rounded-lg border shadow-sm"
            onError={(e) => {
              (e.target as HTMLImageElement).src =
                "https://via.placeholder.com/300x200?text=Image+Not+Found";
            }}
          />
        ))}
      </div>
    ) : (
      <p className="text-sm text-gray-500">
        No images uploaded for this event.
      </p>
    )}
  </div>

  {/* Buttons */}
  <div className="mt-8 flex justify-end gap-3 border-t pt-4">
    <button
      type="button"
      onClick={() => navigate(-1)}
      className="px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-100"
    >
      Back to List
    </button>

    <button
      type="button"
      onClick={() =>
        navigate(`/admin/other-details/events/edit/${event.id}`, {
          state: { item: event, mode: "edit" },
        })
      }
      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
    >
      Edit Event
    </button>
  </div>

</div>

    </div>
  );
};

export default AdminEventsView;
